import type {MutationResult} from '@apollo/client';
import ButtonLabel from '@pinecast/common/ButtonLabel';
import type {ElementConfig} from '@pinecast/common/types';
import {FormattedMessage} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import type * as React from 'react';

import type {ManagedMutationResult} from './hooks';

// Type guard to check if it's an Apollo MutationResult
function isApolloMutationResult(
  state: ManagedMutationResult<any, any> | MutationResult<any>,
): state is MutationResult<any> {
  return 'loading' in state;
}

function getStatus(
  state: ManagedMutationResult<any, any> | MutationResult<any>,
): {error: React.ReactNode; pending: boolean; success: boolean} {
  if (isApolloMutationResult(state)) {
    return {
      error: state.error?.message ?? null,
      pending: state.loading,
      success: state.called && !state.loading && !state.error && !!state.data,
    };
  }
  return {
    error: state.error,
    pending: state.pending,
    success: state.success,
  };
}

const GQLButtonLabel = (props: {
  children: React.ReactNode;
  error?: React.ReactNode;
  position?: ElementConfig<typeof ButtonLabel>['position'];
  state: ManagedMutationResult<any, any> | MutationResult<any>;
  style?: CSS;
  successMessage?: React.ReactNode;
}) => {
  const {error, pending, success} = getStatus(props.state);

  return (
    <ButtonLabel
      message={
        props.error ||
        error ||
        (success
          ? props.successMessage || (
              <FormattedMessage
                description="Success message when something is saved"
                defaultMessage="Saved successfully."
              />
            )
          : null)
      }
      pendingMessage={
        pending ? (
          <FormattedMessage
            description="Announced to screen reader users while something is being saved"
            defaultMessage="Saving…"
          />
        ) : null
      }
      position={props.position}
      style={props.style}
      type={
        props.error || error ? 'negative' : success ? 'positive' : undefined
      }
    >
      {props.children}
    </ButtonLabel>
  );
};

export default GQLButtonLabel;
